import axiosInstance from './http';

function logAndRethrow(endpoint, error) {
    if (error?.response) {
        console.error(`[attachmentApi] ${endpoint} -> ${error.response.status}`, {
            url: error.response.config?.url,
            method: error.response.config?.method,
            params: error.response.config?.params,
            data: error.response.data,
            headers: error.response.headers,
        });
    } else {
        console.error(`[attachmentApi] ${endpoint} -> network/error`, error);
    }
    throw error;
}

export default {
    /**
     * Список документов по сущности.
     * @param {object} params - Параметры запроса
     * @param {string} params.target - 'work' | 'contract' | 'facility'
     * @param {number|string} params.id - ID сущности
     * @param {number} [params.page] - Номер страницы
     * @param {number} [params.page_size] - Количество на странице
     * @returns {Promise<{count: number, results: Array}>}
     */
    async getDocuments({ target, id, page, page_size }) {
        if (!target || id == null) return { count: 0, results: [] };
        try {
            const response = await axiosInstance.get(`${target}/${id}/documents/`, {
                params: { page, page_size },
            });
            return response.data;
        } catch (error) {
            logAndRethrow(`GET /${target}/${id}/documents/`, error);
        }
    },

    /**
     * Список файлов по сущности.
     * @param {object} params - Параметры запроса
     * @param {string} params.target - 'work' | 'contract' | 'facility'
     * @param {number|string} params.id - ID сущности
     * @param {number} [params.page] - Номер страницы
     * @param {number} [params.page_size] - Количество на странице
     * @returns {Promise<{count: number, results: Array}>}
     */
    async getFiles({ target, id, page, page_size }) {
        if (!target || id == null) return { count: 0, results: [] };
        try {
            const response = await axiosInstance.get(`${target}/${id}/files/`, {
                params: { page, page_size },
            });
            return response.data;
        } catch (error) {
            logAndRethrow(`GET ${target}/${id}/files/`, error);
        }
    },
    /**
     * Список типов документов, доступных для сущности
     * @param {object} params - Параметры запроса
     * @param {string} params.target - 'work' | 'contract' | 'facility'
     * @param {string} [params.kind='document'] - 'document' | 'file'
     * @returns {Promise<object>}
     */
    async getDocumentTypes({ target, kind = 'document' }) {
        try {
            const response = await axiosInstance.get('document-types/', {
                params: { target, kind },
            });
            return response.data;
        } catch (error) {
            logAndRethrow('GET document-types/', error);
        }
    },

    /**
     * Шаг 1: Получение presigned URL для загрузки в S3
     * @param {object} params - Параметры запроса
     * @param {string} params.target - Тип сущности
     * @param {number|string} params.id - ID сущности
     * @param {string} params.file_name - Имя файла
     * @param {string} params.content_type - MIME тип
     * @param {number} [params.document_type_id] - ID типа документа
     * @param {string} [params.kind='document'] - 'document' | 'file'
     * @returns {Promise<{url: string, method: string, key: string}>}
     */
    async getPresignedUrl({ target, id, file_name, content_type, document_type_id, kind = 'document' }) {
        try {
            const payload = {
                file_name,
                content_type,
                kind,
                document_type_id,
            };
            const response = await axiosInstance.post(`${target}/${id}/attachments/presign/`, payload);
            return response.data; // { url, method, key }
        } catch (error) {
            logAndRethrow(`POST ${target}/${id}/attachments/presign/`, error);
        }
    },

    /**
     * Шаг 2: Уведомление бэкенда о завершении загрузки
     * @param {object} params - Параметры запроса
     * @param {string} params.target - Тип сущности
     * @param {number|string} params.id - ID сущности
     * @param {string} params.key - S3 ключ файла
     * @param {string} params.file_name - Имя файла
     * @param {string} params.content_type - MIME тип
     * @param {number} params.size - Размер файла в байтах
     * @param {number} [params.document_type_id] - ID типа документа
     * @param {string} [params.kind='document'] - 'document' | 'file'
     * @returns {Promise<object>}
     */
    async completeUpload({ target, id, key, file_name, content_type, size, document_type_id, kind = 'document' }) {
        try {
            const payload = { key, file_name, content_type, size, document_type_id, kind };
            const response = await axiosInstance.post(`${target}/${id}/attachments/complete/`, payload);
            return response.data;
        } catch (error){
            logAndRethrow(`POST ${target}/${id}/attachments/complete/`, error);
        }
    },

    /**
     * Удалить вложение по ID
     * @param {number|string} attachmentId - ID вложения для удаления
     * @returns {Promise<void>}
     */
    async deleteAttachment(attachmentId) {
        try {
            await axiosInstance.delete(`attachments/${attachmentId}/`);
        } catch (error) {
            logAndRethrow(`DELETE attachments/${attachmentId}/`, error);
        }
    },

};